import { EMPTY_STRING } from 'constants/general';
import { createAppSelector } from 'store/hooks';

export const subscriptionSelector = createAppSelector(
  [
    (state) => state.subscription.isSubscriptionChecking,
    (state) => state.user.isTrialUser,
    (state) => state.subscription.isFetchingStripPlans,
    (state) => state.user.showTeamNameModal
  ],
  (
    isSubscriptionChecking,
    isTrialUser,
    isFetchingStripPlans,
    showTeamNameModal
  ) => {
    return {
      isSubscriptionChecking,
      isTrialUser,
      isFetchingStripPlans,
      showTeamNameModal
    };
  }
);

export const subPageContentSelector = createAppSelector(
  [
    (state) =>
      state.modal.cancelSubscriptionModal.isCancelSubscriptionModalOpened,
    (state) => state.subscription.subscriptions,
    (state) => state.subscription.entitlements,
    (state) => state.subscription.activeEntitlements
  ],
  (
    isCancelSubscriptionModalOpened,
    subscriptions,
    entitlements,
    activeEntitlements
  ) => {
    return {
      isCancelSubscriptionModalOpened,
      subscriptions,
      entitlements,
      activeEntitlements
    };
  }
);

export const toDoFocusEndSelector = createAppSelector(
  [(state) => state.setting.isTokenAddedToInterceptor],
  (isTokenAddedToInterceptor) => {
    return {
      isTokenAddedToInterceptor
    };
  }
);

export const toDoFocusEndTasksSelector = createAppSelector(
  [
    (state) => state.toDo.isTaskUpdating,
    (state) => state.toDo.isTaskSubmitting,
    (state) => state.setting.platform
  ],
  (isTaskUpdating, isTaskSubmitting, platform) => {
    return {
      isTaskUpdating,
      isTaskSubmitting,
      platform
    };
  }
);

export const toDoFocusEndTaskSelector = createAppSelector(
  [
    (state) => state.toDo.isTaskUpdating,
    (state) => state.toDo.isTaskSubmitting
  ],
  (isTaskUpdating, isTaskSubmitting) => {
    return {
      isTaskUpdating,
      isTaskSubmitting
    };
  }
);

export const motivationalTonesSelector = createAppSelector(
  [
    (state) => state.setting.aiTone,
    (state) => state.user.isMotivationalSummaryLoading,
    (state) => state.user.isMotivationalSummaryStopGenerating
  ],
  (
    aiTone,
    isMotivationalSummaryLoading,
    isMotivationalSummaryStopGenerating
  ) => {
    return {
      aiTone,
      isMotivationalSummaryLoading,
      isMotivationalSummaryStopGenerating
    };
  }
);

export const motivationalSummarySelector = createAppSelector(
  [
    (state) => state.user.motivationalSummary,
    (state) => state.user.isMotivationalSummaryLoading,
    (state) => state.user.openAIPrompt,
    (state) => state.user.isMotivationalSummaryStopGenerating,
    (state) => state.user?.details,
    (state) => state.general.hasMotivationalDelayElapsed
  ],
  (
    motivationalSummary,
    isMotivationalSummaryLoading,
    openAIPrompt,
    isMotivationalSummaryStopGenerating,
    userInfo,
    hasMotivationalDelayElapsed
  ) => {
    return {
      motivationalSummary,
      isMotivationalSummaryLoading,
      openAIPrompt,
      isMotivationalSummaryStopGenerating,
      userInfo,
      hasMotivationalDelayElapsed
    };
  }
);

export const motivationalPageSelector = createAppSelector(
  [
    (state) => state.setting.aiTone,
    (state) => state.setting.language,
    (state) => state.modal.longTermGoalsModal.isLongTermGoalsModalOpened,
    (state) => state.user.isMotivationalSummaryLoading,
    (state) => state.user.isMotivationalSummaryStopGenerating
  ],
  (
    aiTone,
    language,
    isLongTermGoalsModalOpened,
    isMotivationalSummaryLoading,
    isMotivationalSummaryStopGenerating
  ) => {
    return {
      aiTone,
      language,
      isLongTermGoalsModalOpened,
      isMotivationalSummaryLoading,
      isMotivationalSummaryStopGenerating
    };
  }
);

export const motivationalPageWrapperSelector = createAppSelector(
  [
    (state) => state.setting.themeMode,
    (state) => state.setting.platform,
    (state) => state.setting.font
  ],
  (themeMode, platform, font) => {
    return {
      themeMode,
      platform,
      font
    };
  }
);

export const motivationalSummaryPromptSelector = createAppSelector(
  [
    (state) => state.user.isMotivationalSummaryStopGenerating,
    (state) => state.user.details,
    (state) => state.user.openAIPrompt
  ],
  (isMotivationalSummaryStopGenerating, userInfo, openAIPrompt) => {
    return {
      isMotivationalSummaryStopGenerating,
      userInfo,
      openAIPrompt
    };
  }
);

export const layoutSelector = createAppSelector(
  [
    (state) => state.user.details,
    (state) => state.subscription?.entitlements || {},
    (state) => state.user.isTrialUser,
    (state) => state.modal.confirmAction.showConfirmModal,
    (state) => state.user.areUserDetailsLoading,
    (state) => state.subscription.activeEntitlements,
    (state) => state.user.auth0User,
    (state) => state.setting.localDeviceSettings,
    (state) => state.setting.language,
    (state) => state.team.teams,
    (state) => state.team.teamsInitialized
  ],
  (
    userInfo,
    entitlements,
    isTrialUser,
    showConfirmModal,
    areUserDetailsLoading,
    activeEntitlements,
    auth0User,
    localDeviceSettings,
    language,
    teams,
    teamsInitialized
  ) => {
    return {
      userInfo,
      entitlements,
      isTrialUser,
      showConfirmModal,
      areUserDetailsLoading,
      activeEntitlements,
      auth0User,
      localDeviceSettings,
      language,
      teams,
      teamsInitialized
    };
  }
);

export const getSupportSelector = createAppSelector(
  [
    (state) => state.user.details?.id,
    (state) => state.user.details?.email,
    (state) => state.subscription.entitlements || {},
    (state) => state.user.isTrialUser
  ],
  (userId, userEmail, entitlements, isTrialUser) => {
    return {
      userId,
      userEmail,
      entitlements,
      isTrialUser
    };
  }
);

export const navBarSelector = createAppSelector(
  [
    (state) => state.subscription?.activeEntitlements ?? [],
    (state) => state.team.teams ?? [],
    (state) => state.user.adminForTeams
  ],
  (entitlements, adminForTeams, userTeams) => {
    return { entitlements, adminForTeams, userTeams };
  }
);

export const isUserAdminSelector = createAppSelector(
  [(state) => state.user.isUserAdmin],
  (isUserAdmin) => {
    return isUserAdmin;
  }
);

export const emailVerificationBannerSelector = createAppSelector(
  [
    (state) => state.user.auth0User?.email,
    (state) => state.user.isSendingEmailVerification,
    (state) => state.setting.language
  ],
  (email, isSendingEmailVerification, language) => {
    return { email, isSendingEmailVerification, language };
  }
);

export const profilePageSelector = createAppSelector(
  [
    (state) => state.setting.isAccountDeleted,
    (state) => state.setting.isRequestDataConfirmationModalShown,
    (state) => state.setting.isAccountDeleteConfirmationModalShown,
    (state) => state.subscription.entitlements,
    (state) => state.subscription.activeEntitlements
  ],
  (
    isAccountDeleted,
    isRequestDataConfirmationModalShown,
    isAccountDeleteConfirmationModalShown,
    entitlements,
    activeEntitlements
  ) => {
    return {
      isAccountDeleted,
      isRequestDataConfirmationModalShown,
      isAccountDeleteConfirmationModalShown,
      entitlements,
      activeEntitlements
    };
  }
);

export const profileTrialEntitlementsSelector = createAppSelector(
  [
    (state) => state.subscription.entitlements,
    (state) => state.subscription.activeEntitlements
  ],
  (entitlements, activeEntitlements) => {
    return {
      entitlements,
      activeEntitlements
    };
  }
);

export const profilePictureSelector = createAppSelector(
  [
    (state) => state.user.details?.metadata?.profile_image?.url,
    (state) => state.user.details?.username,
    (state) => state.user.isUpdatingUserProfileImage
  ],
  (profileImageUrl, username, isUpdatingUserProfileImage) => {
    return {
      profileImageUrl,
      username,
      isUpdatingUserProfileImage
    };
  }
);

export const profileImageErrorLocaleSelector = createAppSelector(
  [(state) => state.setting.language],
  (language) => {
    return {
      language
    };
  }
);

export const profileDescriptionSelector = createAppSelector(
  [
    (state) => state.user.details?.metadata?.description,
    (state) => state.user.isUpdatingUserProfileDescription,
    (state) => state.subscription.subscriptionInfo?.userId
  ],
  (description, isUpdatingUserProfileDescription, userId) => {
    return {
      description,
      isUpdatingUserProfileDescription,
      userId
    };
  }
);

export const userConfirmationModalSelector = createAppSelector(
  [
    (state) => state.setting.language,
    (state) => state.setting.isRequestDataProcessing,
    (state) => state.setting.isAccountDeleting,
    (state) => state.setting.isAccountDeleted
  ],
  (
    language,
    isRequestDataProcessing,
    isAccountDeleting,
    isAccountDeleted
  ) => {
    return {
      language,
      isRequestDataProcessing,
      isAccountDeleting,
      isAccountDeleted
    };
  }
);

export const timezoneCardSelector = createAppSelector(
  [
    (state) => state.user.details?.timezone,
    (state) => state.user.isUpdatingUserTimezone
  ],
  (timezone, isUpdatingUserTimezone) => {
    return {
      timezone,
      isUpdatingUserTimezone
    };
  }
);

export const routinesAndFocusBlocksSelector = createAppSelector(
  [
    (state) =>
      state.user.onboardingStats.morning_routine_completion_streak_days,
    (state) =>
      state.user.onboardingStats.evening_routine_completion_streak_days,
    (state) => state.user.onboardingStats.focus_mode_completion_streak_days,
    (state) =>
      state.user.onboardingStats.average_morning_routines_completion_percentage,
    (state) =>
      state.user.onboardingStats.average_evening_routines_completion_percentage,
    (state) =>
      state.user.onboardingStats.average_num_focus_modes_completed_per_day,
    (state) => state.user.onboardingStats.routines_threshold,
    (state) => state.user.onboardingStats.focus_modes_threshold,
    (state) => state.user.areOnboardingStatsLoading
  ],
  (
    morning_routine_completion_streak_days,
    evening_routine_completion_streak_days,
    focus_mode_completion_streak_days,
    average_morning_routines_completion_percentage,
    average_evening_routines_completion_percentage,
    average_num_focus_modes_completed_per_day,
    routines_threshold,
    focus_modes_threshold,
    areOnboardingStatsLoading
  ) => {
    return {
      morning_routine_completion_streak_days,
      evening_routine_completion_streak_days,
      focus_mode_completion_streak_days,
      average_morning_routines_completion_percentage,
      average_evening_routines_completion_percentage,
      average_num_focus_modes_completed_per_day,
      routines_threshold,
      focus_modes_threshold,
      areOnboardingStatsLoading
    };
  }
);

export const OnboardingStatsLoadingSelector = createAppSelector(
  [(state) => state.user.areOnboardingStatsLoading],
  (areOnboardingStatsLoading) => {
    return areOnboardingStatsLoading;
  }
);

export const longTermGoalsSelector = createAppSelector(
  [
    (state) => state.user.details?.long_term_goals,
    (state) => state.user.isFetchingLogTermGoal
  ],
  (long_term_goals, isFetchingLogTermGoal) => {
    return {
      long_term_goals,
      isFetchingLogTermGoal
    };
  }
);

export const isLogTermGoalUpdatingSelector = createAppSelector(
  [(state) => state.user.isLogTermGoalUpdating],
  (isLogTermGoalUpdating) => {
    return { isLogTermGoalUpdating };
  }
);

export const longTermGoalsActionsSelector = createAppSelector(
  [
    (state) => state.user.isLogTermGoalUpdating,
    (state) => state.user.details?.long_term_goals
  ],
  (isLogTermGoalUpdating, longTermGoals) => {
    return { isLogTermGoalUpdating, longTermGoals };
  }
);

export const onboardingStatsSelector = createAppSelector(
  [(state) => state.user.onboardingStats],
  (onboardingStats) => {
    return onboardingStats;
  }
);

export const onboardingStatsGoalsSelector = createAppSelector(
  [(state) => state.user.onboardingStats],
  (onboardingStats) => {
    return onboardingStats;
  }
);

export const leaderBoardSelector = createAppSelector(
  [
    (state) => state.setting.isLeaderBoardFetching,
    (state) => state.setting.leaders,
    (state) => state.user.areUserDetailsLoading
  ],
  (isLeaderBoardFetching, leaders, areUserDetailsLoading) => {
    return { isLeaderBoardFetching, leaders, areUserDetailsLoading };
  }
);

export const userNameInfoBoardSelector = createAppSelector(
  [
    (state) => state.setting.isLeaderBoardFetching,
    (state) => state.setting.leaders,
    (state) => state.user.isUsernameUpdating,
    (state) => state.user.details?.username ?? EMPTY_STRING
  ],
  (isLeaderBoardFetching, leaders, isUsernameUpdating, userName) => {
    return { isLeaderBoardFetching, leaders, isUsernameUpdating, userName };
  }
);

export const leaderBoardWrapperSelector = createAppSelector(
  [(state) => state.user.details?.username ?? EMPTY_STRING],
  (userName) => {
    return userName;
  }
);

export const auth0UserSelector = createAppSelector(
  [(state) => state.user.auth0User],
  (auth0User) => {
    return auth0User;
  }
);

export const userPrivacyFormSelector = createAppSelector(
  [(state) => state.user.isUpdatingUserPrivacyPolicy],
  (isUpdatingUserPrivacyPolicy) => {
    return isUpdatingUserPrivacyPolicy;
  }
);

export const productivitySelector = createAppSelector(
  [
    (state) => state.user.completedFocusBlocks,
    (state) => state.user.isCompletedFocusBlocksFetching
  ],
  (completedFocusBlocks, isCompletedFocusBlocksFetching) => {
    return { completedFocusBlocks, isCompletedFocusBlocksFetching };
  }
);
